<script setup lang="ts">
import { defineEmits, defineProps, ref, watch } from 'vue'
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'
import { RadioGroupSelectOption } from '../../typings'
import { useFieldHelper } from './useBaseInput'

const props = defineProps<{
    modelValue: string | boolean
    label: string
    name: string
    options: RadioGroupSelectOption[]
    // required?: boolean
    // required2?: boolean
    rules?: any
}>()

const { handleChange } = useFieldHelper(props.name, props.rules, { initialValue: props.modelValue })

const emit = defineEmits(['update:modelValue'])

function updateValue(value) {
    handleChange(value)
    emit('update:modelValue', value)
}

const selected = ref(props.modelValue)

watch(
    () => props.modelValue,
    (val) => (selected.value = val),
    { deep: true }
)
watch(
    () => selected,
    (val) => updateValue(val.value),
    { deep: true }
)
</script>

<template>
    <RadioGroup v-model="selected">
        <RadioGroupLabel class="sr-only"> Pricing options </RadioGroupLabel>
        <div class="relative -space-y-px rounded-md bg-white">
            <RadioGroupOption
                v-for="(option, optionIdx) in options"
                :key="option.key"
                v-slot="{ checked, active }"
                as="template"
                :value="option">
                <div
                    :class="[
                        optionIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '',
                        optionIdx === options.length - 1 ? 'rounded-bl-md rounded-br-md' : '',
                        checked ? 'z-10 border-primary-200 bg-primary-50' : 'border-gray-200',
                        'relative flex cursor-pointer flex-col border p-4 focus:outline-none md:grid md:grid-cols-2 md:pl-4 md:pr-6',
                    ]">
                    <span class="flex items-center text-sm">
                        <span
                            :class="[
                                checked ? 'border-transparent bg-primary-600' : 'border-gray-300 bg-white',
                                active ? 'ring-2 ring-primary-500 ring-offset-2' : '',
                                'flex h-4 w-4 items-center justify-center rounded-full border',
                            ]"
                            aria-hidden="true">
                            <span class="h-1.5 w-1.5 rounded-full bg-white" />
                        </span>
                        <RadioGroupLabel
                            as="span"
                            :class="[checked ? 'text-primary-900' : 'text-gray-900', 'ml-3 font-medium']"
                            >{{ option.label }}</RadioGroupLabel
                        >
                    </span>
                    <RadioGroupDescription
                        as="div"
                        :class="[
                            checked ? 'text-primary-700' : 'text-gray-500',
                            'ml-6 pl-1 text-sm md:ml-0 md:pl-0 md:text-right',
                        ]">
                        {{ option.description }}
                    </RadioGroupDescription>
                </div>
            </RadioGroupOption>
        </div>
    </RadioGroup>
</template>
